import { useEffect, useMemo, useState } from "react";

import CheckBoxIcon from "@mui/icons-material/CheckBox";
import CheckBoxOutlineBlankIcon from "@mui/icons-material/CheckBoxOutlineBlank";
import {
  Autocomplete,
  Checkbox,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  SelectChangeEvent,
  Stack,
  TextField,
} from "@mui/material";
import { GridFilterOperator } from "@mui/x-data-grid";

export type SelectOption = {
  id: string;
  label: string;
};

const icon = <CheckBoxOutlineBlankIcon fontSize="small" />;
const checkedIcon = <CheckBoxIcon fontSize="small" />;

const selectFilter = (
  optionsMap: Map<string, string>,
  getValueAsString: (value: string) => string,
  formatter?: (value: string, label: string) => string,
): GridFilterOperator<any, string> => {
  const validate = (value: string) => value && value !== "Please Select";

  return {
    label: "Equals",
    value: "equals",
    getApplyFilterFn: ({ field, value, operator }) => {
      if (!field || !value || !operator) return null;
      return (cellValue) => cellValue === value;
    },
    InputComponent: (props) => {
      const { item, applyValue, focusElementRef } = props;
      const [value, setValue] = useState(item.value ?? "");
      const [isValid, setIsValid] = useState(validate(value));
      const options = useMemo(
        () =>
          Array.from(optionsMap.entries()).map(([value, label]) => (
            <MenuItem key={value} value={value}>
              {formatter ? formatter(value, label) : label}
            </MenuItem>
          )),
        [],
      );

      const handleChange = (event: SelectChangeEvent) => {
        const newValue = event.target.value;
        const isValid = validate(newValue);
        setValue(newValue);
        setIsValid(isValid);
        applyValue({ ...item, value: newValue, isValid });
      };

      useEffect(() => {
        if (item.value === undefined) {
          setValue("Please Select");
          setIsValid(false);
        }
      }, [item.value]);

      return (
        <FormControl variant="standard" error={!isValid}>
          <InputLabel id="grid-filter-select-label" shrink>
            Value *
          </InputLabel>
          <Select
            labelId="grid-filter-select-label"
            required
            value={value}
            label="Value *"
            onChange={handleChange}
            sx={{
              minWidth: { xs: "auto", sm: 400 }, // Full width on smaller screens
            }}
            inputRef={focusElementRef}
            error={!isValid}
          >
            <MenuItem key="" value="Please Select">
              <em>Please Select</em>
            </MenuItem>
            {options}
          </Select>
        </FormControl>
      );
    },
    getValueAsString,
  };
};

const multiSelectFilter = (
  options: SelectOption[],
  getValueAsString: (values: string[]) => string,
  formatter?: (value: string, label: string) => string,
): GridFilterOperator<any, string> => {
  const validate = (values: string[]) => values && values.length > 0;

  return {
    label: "Contains",
    value: "contains",
    getApplyFilterFn: ({ field, value, operator }) => {
      if (!field || !value || !operator) return null;
      return (cellValue) => cellValue === value;
    },
    InputComponent: (props) => {
      const { item, applyValue, focusElementRef } = props;
      const [value, setValue] = useState(
        item.value?.reduce(
          (acc: SelectOption[], id: string) => [...acc, options.find((o) => o.id === id)],
          [],
        ) ?? [],
      );
      const [isValid, setIsValid] = useState(validate(value));

      const handleChange = (_event: object, values: SelectOption[]) => {
        setValue(values);
        const newValues = values.map((value) => value.id);
        const isValid = validate(newValues);
        setIsValid(isValid);
        applyValue({ ...item, value: newValues, isValid });
      };

      const getLabel = (value: string, label: string) => (formatter ? formatter(value, label) : label);

      useEffect(() => {
        if (item.value === undefined) {
          setValue([]);
          setIsValid(false);
        }
      }, [item.value]);

      return (
        <Stack
          spacing={1}
          sx={{
            minWidth: { xs: "100%", sm: 400 }, // Full width on smaller screens
            width: { xs: "200px" },
          }}
        >
          <Autocomplete
            multiple
            disableCloseOnSelect
            options={options}
            getOptionLabel={(option: SelectOption) => {
              return getLabel(option.id, option.label);
            }}
            getOptionKey={(option: SelectOption) => option.id}
            onChange={handleChange}
            value={value}
            isOptionEqualToValue={(option, value) => option.id === value.id}
            renderOption={(props, option, { selected }) => {
              const { key, ...optionProps } = props as any;
              return (
                <li key={key} {...optionProps}>
                  <Checkbox
                    icon={icon}
                    checkedIcon={checkedIcon}
                    sx={{ marginRight: 8 }}
                    checked={selected}
                  />
                  {getLabel(option.id, option.label)}
                </li>
              );
            }}
            renderInput={(params) => (
              <TextField
                {...params}
                variant="standard"
                label="Value *"
                placeholder="Please Select"
                InputLabelProps={{ shrink: true }}
                error={!isValid}
                autoFocus
                inputRef={focusElementRef}
                fullWidth
                sx={{
                  paddingTop: "3px",
                }}
              />
            )}
            size="small"
          />
        </Stack>
      );
    },
    getValueAsString,
  };
};

const enumFilter = (
  enumObject: any,
  getValueAsString: (value: string) => string,
): GridFilterOperator<any, string> => {
  const enumKeysMap = Object.keys(enumObject)
    .filter((key) => isNaN(Number(key)))
    .reduce((map, item) => {
      map.set(item, item);
      return map;
    }, new Map<string, string>());

  return selectFilter(enumKeysMap, getValueAsString);
};

const multiEnumFilter = (
  enumObject: any,
  getValueAsString: (values: string[]) => string,
): GridFilterOperator<any, string> => {
  const enumOpptions = Object.keys(enumObject)
    .filter((key) => isNaN(Number(key)))
    .map((item) => {
      return { id: item, label: item };
    });

  return multiSelectFilter(enumOpptions, getValueAsString);
};

const mapToSelectOptions = (map: Map<string, string>): SelectOption[] => {
  return Array.from(map.entries()).map(([id, label]) => {
    return { id, label };
  });
};

export { enumFilter, multiEnumFilter, selectFilter, multiSelectFilter, mapToSelectOptions };
